import {
	AfterViewInit,
	ChangeDetectorRef,
	Component,
	ElementRef,
	EventEmitter,
	forwardRef,
	Input,
	Output,
	ViewChild,
} from '@angular/core';
import { NG_VALUE_ACCESSOR } from '@angular/forms';
import { ControlValueAccessorBase } from '@shared/bases/control-value-accessor.base';
import { SharedModule } from '@shared/modules/shared.module';
import { LucideAngularModule } from 'lucide-angular';

@Component({
	selector: 'app-textarea',
	imports: [SharedModule, LucideAngularModule],
	templateUrl: './textarea.component.html',
	styleUrl: './textarea.component.scss',
	providers: [
		{
			provide: NG_VALUE_ACCESSOR,
			useExisting: forwardRef(() => TextareaComponent),
			multi: true,
		},
	],
})
export class TextareaComponent extends ControlValueAccessorBase<string> implements AfterViewInit {
	@Input() placeholder = '';
	@Input() size: 'small' | 'medium' | 'large' | 'adaptable-small' | 'adaptable-medium' | 'adaptable-large' = 'large';
	@Input() disabled = false;
	@Input() error = false;
	@Input() errorMessage = '';
	@Input() label = '';
	@Input() helperText = '';
	@Input() maxHeight = 150; // Max height in pixels for adaptable sizes
	@Input() rows?: number; // Number of visible text lines (HTML rows attribute)
	@Input() maxLength: number | null = null; // Maximum character count
	@Input() leadingIcon = ''; // Lucide icon name for left button (e.g., 'plus', 'paperclip')
	@Output() leadingIconClick = new EventEmitter<void>();
	@Output() focused = new EventEmitter<void>();

	@ViewChild('textareaRef') textareaRef!: ElementRef<HTMLTextAreaElement>;

	constructor(private cdRef: ChangeDetectorRef) {
		super();
	}

	ngAfterViewInit(): void {
		if (this.isAdaptable() && this.textareaRef) {
			this.autoResize();
		}
	}

	/**
	 * Check if the current size is an adaptable variant
	 */
	isAdaptable(): boolean {
		return this.size.startsWith('adaptable-');
	}

	/**
	 * Callback triggered on native textarea input change.
	 * Updates the internal value and notifies Angular.
	 */
	onInput(event: Event): void {
		const textarea = event.target as HTMLTextAreaElement;
		this.value = textarea.value;

		// Auto-resize for adaptable sizes
		if (this.isAdaptable()) {
			this.autoResize();
		}
	}

	/**
	 * Handle leading icon button click
	 */
	onLeadingIconClick(): void {
		if (!this.disabled) {
			this.leadingIconClick.emit();
		}
	}

	/**
	 * Handles focus event and emits it
	 */
	onFocus(): void {
		this.focused.emit();
	}

	/**
	 * Get the initial height based on the adaptable size
	 */
	private getInitialHeight(): number {
		switch (this.size) {
			case 'adaptable-large':
				return 64;
			case 'adaptable-medium':
				return 56;
			case 'adaptable-small':
				return 48;
			default:
				return 64;
		}
	}

	/**
	 * Auto-resize the textarea based on content (for adaptable sizes)
	 */
	private autoResize(): void {
		if (!this.textareaRef) return;

		const textarea = this.textareaRef.nativeElement;
		const initialHeight = this.getInitialHeight();

		// If empty, we measure against the placeholder so it shows in full: a
		// 2-line placeholder doesn't fit the initial height (1 line) and overflowed.
		const measuringPlaceholder = !textarea.value;
		if (measuringPlaceholder) {
			textarea.value = textarea.placeholder ?? '';
		}

		// Reset to 'auto' to measure the real scrollHeight of the content (or placeholder).
		textarea.style.height = 'auto';
		const contentHeight = textarea.scrollHeight;

		if (measuringPlaceholder) {
			textarea.value = '';
		}

		// Grows from the initial height up to the max; scrollbar only if it exceeds the max.
		const newHeight = Math.max(initialHeight, Math.min(contentHeight, this.maxHeight));
		textarea.style.height = `${newHeight}px`;
		textarea.style.overflowY = contentHeight > this.maxHeight ? 'auto' : 'hidden';
	}

	/**
	 * Allows Angular to set the disabled state of the textarea.
	 */
	override setDisabledState(isDisabled: boolean): void {
		this.disabled = isDisabled;
		this.cdRef.markForCheck();
	}

	/**
	 * Ensures the DOM textarea reflects the latest value written from Angular.
	 */
	override writeValue(value: string): void {
		super.writeValue(value);
		this.cdRef.markForCheck();
		if (this.textareaRef) {
			this.textareaRef.nativeElement.value = value ?? '';

			// Reset height when value is cleared (for adaptable sizes)
			if (this.isAdaptable()) {
				setTimeout(() => this.autoResize(), 0);
			}
		}
	}
}
